import cn from 'classnames';
import type { FC, MouseEvent } from 'react';
import React, { useRef } from 'react';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';
import { horizontalScrollTabs } from '@alfa-bank/site-helpers-common/scroll/horizontal-scroll-tabs';

import type { TTagsHeader } from './models';
import { useFirstHorizontalScroll } from './hooks/use-first-horizontal-scroll';

export const TagsHeader: FC<TTagsHeader> = ({
    Img,
    Tag,
    align,
    className,
    column = false,
    dataTestId = 'tabs-list',
    inline = true,
    onChange,
    scroll,
    selectedID,
    size,
    styles,
    theme,
    titles,
}) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const selectedTabRef = useFirstHorizontalScroll(wrapperRef);

    const onClick = (
        event: MouseEvent<HTMLButtonElement>,
        id: number | string,
        dataLayer?: { [key: string]: string }
    ) => {
        horizontalScrollTabs(wrapperRef)(event);

        if (dataLayer) {
            sendEvent(dataLayer);
        }

        onChange({ event, id });
    };

    return (
        <div
            ref={wrapperRef}
            className={cn(
                styles.tagsContainer,
                {
                    [styles.alignCenter]: align === 'center',
                    [styles.scroll]: scroll,
                    [styles.column]: column,
                },
                className
            )}
            data-test-id={dataTestId}
            role="tablist"
        >
            {titles.map((item, i) => {
                const leftAddons = item.iconSrc && <Img key={item.id} src={item.iconSrc} />;
                const currentRef =
                    item.id === selectedID ? { forwardedRef: selectedTabRef } : undefined;

                return (
                    <Tag
                        key={item.id}
                        classNames={cn(styles.external, styles.tag)}
                        dataTestId={dataTestId && `${dataTestId}-tabTitle-${i}`}
                        inline={inline}
                        label={item.title}
                        leftAddons={leftAddons}
                        onClick={event => onClick(event, item.id, item.dataLayer)}
                        returnEvent
                        role="tab"
                        selected={selectedID}
                        size={size}
                        tabIndex={0}
                        theme={theme}
                        value={item.id}
                        {...currentRef}
                    />
                );
            })}
        </div>
    );
};
